import {
  Component,
  OnInit,
  isDevMode,
  OnDestroy,
  PLATFORM_ID,
  Inject,
  ViewChild,
  TemplateRef,
  ViewContainerRef,
  HostListener,
} from '@angular/core'
import {
  MarketService,
  IProductListing,
} from '../../../services/market.service'
import { isPlatformBrowser } from '@angular/common'
import { TranslateService } from '@ngx-translate/core'
import { ActivatedRoute, Router } from '@angular/router'
import { SnackbarService } from '../../../services/snackbar.service'
import { AuthenticationService } from '../../../services/authentication.service'
import { User } from '../../../models/user.model'
import { ProjectService } from '../../../services/project.service'
import { IProject } from '../../../models/project.model'
import { IBreadCrumb } from '../../../models/breadcrumb.model'
import { Subscription } from 'rxjs'
import { UtilsService } from '../../../services/utils.service'

@Component({
  selector: 'app-marketplace',
  templateUrl: './marketplace.component.html',
  styleUrls: ['./marketplace.component.scss'],
})
export class MarketplaceComponent implements OnInit, OnDestroy {
  @ViewChild('productsBatch', { read: TemplateRef, static: true })
  productsBatch: TemplateRef<any>
  @ViewChild('vc', { read: ViewContainerRef, static: true })
  vc: ViewContainerRef

  public currentUser: User

  public Products: IProductListing[]
  public productsLoaded = false
  public displayedProducts: IProductListing[] = []
  public filteredProducts: IProductListing[] = []
  public productNotifications: any[] = []

  public projects: IProject[] = []

  public breadCrumbs: IBreadCrumb[] = []

  public buttonClicked: boolean

  public tabs = [
    {
      label: 'all',
      page: 1,
    },
    {
      label: 'tools',
      page: 1,
    },
    {
      label: 'server',
      page: 1,
    },
    {
      label: 'os',
      page: 1,
    },
  ]

  public selectedTab = 0
  public onlyAvailable = true
  public namefilter: string

  public windowsSize = 50
  public jumpNumber = 0

  public filterProducts_debounced: Function

  private subscriptions: Subscription

  constructor(
    @Inject(PLATFORM_ID) private platformId: any,
    private marketService: MarketService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private translate: TranslateService,
    private snackbarService: SnackbarService,
    private authService: AuthenticationService,
    private projectService: ProjectService,
    private utilsService: UtilsService
  ) {
    this.subscriptions = new Subscription()

    this.buttonClicked = false

    this.filterProducts_debounced = this.utilsService.debounce(() => {
      this.filterProducts()
      this.updateURL()
    }, 300)
  }

  async ngOnInit() {
    if (isPlatformBrowser(this.platformId)) {
      this.authService.userData.subscribe((user) => (this.currentUser = user))
    }

    this.subscriptions.add(
      this.marketService.products().subscribe(
        (products) => {
          this.Products = products
          this.filterProducts()
          this.productsLoaded = true
          this.getProductNotifications()
          this.createBreadcrumb([
            { label: 'marketplace.breadcrumb.marketplace', url: '' },
          ])
        },
        (_) => {
          this.snackbarService.showSnackbar(
            'error.error_retrieving',
            'error.button.close',
            'error-snack'
          )
        }
      )
    )

    if (isPlatformBrowser(this.platformId)) {
      this.onlyAvailable =
        this.activatedRoute.snapshot.queryParams.onlyAvailable === false ||
        this.activatedRoute.snapshot.queryParams.onlyAvailable === 'false'
          ? false
          : true

      this.activatedRoute.queryParams.subscribe(
        (param: any) => {
          if (param.q) {
            this.namefilter = param.q
            this.filterProducts_debounced()
          }
          this.tabs.forEach((tab, index) => {
            if (tab.label === (param.class || 'all')) {
              this.selectedTab = index
            }
          })
        },
        (error) => {
          if (isDevMode) {
            // eslint-disable-next-line no-console
            console.error(error)
          }
        }
      )

      if (this.currentUser) {
        this.projects = await this.projectService
          .listProjects()
          .toPromise()
          .then((result: IProject[]) =>
            result.sort((a, b) => a.name.localeCompare(b.name))
          )
      }
    }
  }

  ngOnDestroy() {
    if (this.subscriptions) {
      this.subscriptions.unsubscribe()
      this.subscriptions = null
    }
  }

  filterProducts(): void {
    const className = this.tabs[this.selectedTab].label

    if (this.Products) {
      this.filteredProducts = this.Products.filter((item: IProductListing) => {
        if (className && !(className === 'all')) {
          if (item.class.indexOf(className) === -1) {
            return false
          }
        }
        if (this.onlyAvailable && !item.available) {
          return false
        }
        if (this.namefilter) {
          const productString = (item.vendor + item.name + item.h).toLowerCase()
          const nameConditions = this.namefilter.toLowerCase().split(' ')
          for (let i = 0; i < nameConditions.length; i++) {
            if (productString.toLowerCase().indexOf(nameConditions[i]) === -1) {
              return false
            }
          }
        }
        return true
      }).sort((a, b) => a.name.localeCompare(b.name))

      this.renderProductsBatch(true)
    }
  }

  getProductNotifications() {
    if (this.currentUser && isPlatformBrowser(this.platformId)) {
      this.subscriptions.add(
        this.marketService.getProductNotifications().subscribe(
          (notifications) => {
            this.productNotifications = notifications
            this.Products.forEach((p) => {
              const pn = this.productNotifications.find((e) => {
                return e.product_id === p.id && (e.state === 0 || e.state === 1)
              })
              p['product_notification_state'] = pn ? pn.state : null
            })
          },
          (_) => {
            this.snackbarService.showSnackbar(
              'error.error_retrieving',
              'error.button.close',
              'error-snack'
            )
          }
        )
      )
    }
  }

  isProductNotificationCreated(productId) {
    const index = this.productNotifications.findIndex(
      (e) => e.product_id === productId && (e.state === 0 || e.state === 1)
    )
    return index >= 0
  }

  toggleProductNotification(productId) {
    if (!this.isProductNotificationCreated(productId)) {
      this.createProductNotification(productId)
    } else {
      this.cancelProductNotification(productId)
    }
  }

  createProductNotification(productId) {
    const data = {
      product_id: productId,
      notification_type: 's',
      state: 0,
    }
    this.subscriptions.add(
      this.marketService.createProductNotification(data).subscribe(
        (_) => {
          this.snackbarService.showSnackbar(
            'marketplace.notification.success.alert_created',
            'marketplace.notification.button.close',
            'success-snack'
          )
          this.getProductNotifications()
        },
        (_) => {
          this.snackbarService.showSnackbar(
            'marketplace.notification.error.cannot_create_alert',
            'marketplace.notification.button.close',
            'error-snack'
          )
        }
      )
    )
  }

  cancelProductNotification(productId) {
    const data = {
      state: 3,
    }
    this.subscriptions.add(
      this.marketService.updateProductNotification(productId, data).subscribe(
        (_) => {
          this.snackbarService.showSnackbar(
            'marketplace.notification.success.alert_canceled',
            'marketplace.notification.button.close',
            'success-snack'
          )
          this.getProductNotifications()
        },
        (_) => {
          this.snackbarService.showSnackbar(
            'marketplace.notification.error.cannot_cancel_alert',
            'marketplace.notification.button.close',
            'error-snack'
          )
        }
      )
    )
  }

  updateURL() {
    this.router.navigate([this.translate.currentLang, 'buy'], {
      queryParams: {
        class: this.tabs[this.selectedTab].label,
        onlyAvailable: this.onlyAvailable,
        ...(this.namefilter && { q: this.namefilter }),
      },
    })
  }

  createBreadcrumb(data: IBreadCrumb[]) {
    this.breadCrumbs = []
    this.breadCrumbs.push({ label: 'home.breadcrumb.home', url: '/' })
    this.breadCrumbs = this.breadCrumbs.concat(data)
  }

  renderProductsBatch(isInitialLoading = false) {
    if (isInitialLoading) {
      this.jumpNumber = 0
      if (this.vc?.length) {
        this.vc.clear()
      }
    } else {
      this.jumpNumber++
    }

    const productsBatchCtx = {
      items: this.filteredProducts.slice(
        this.jumpNumber * this.windowsSize,
        this.jumpNumber * this.windowsSize + this.windowsSize
      ),
    }

    this.vc.createEmbeddedView(this.productsBatch, productsBatchCtx)
    this.productsLoaded = true
  }

  clearSearchInput() {
    this.namefilter = ''
    this.filterProducts_debounced()
  }

  @HostListener('window:scroll', ['$event'])
  public onWindowScroll(e): void {
    const pos =
      (document.documentElement.scrollTop || document.body.scrollTop) +
      document.documentElement.offsetHeight
    const max = document.documentElement.scrollHeight - 500

    if (
      this.filteredProducts.length >
      this.jumpNumber * this.windowsSize + this.windowsSize
    ) {
      this.productsLoaded = false
    } else {
      this.productsLoaded = true
    }

    if (pos > max) {
      if (
        this.filteredProducts.length >
        this.jumpNumber * this.windowsSize + this.windowsSize
      ) {
        this.renderProductsBatch()
      }
    }
  }
}
